import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';

import {
  OpportunityHead,
  CreatedByInfo,
  SimilarOpportunities,
  AboutEvent,
  EventVenue,
  DescriptionSummaryTabs,
  FloatingActionButtons,
  OpportunityBanner,
} from 'components/opportunity-page';
import { ExternalSignupBanner } from 'components/candidate/ExternalSignupBanner/ExternalSignupBanner.component';
import HamburgerNavMenu from 'components/candidate/HamburgerNavMenu/HamburgerNavMenu.component';
import IconButton from 'components/IconButton';
import { GPTActionMenu } from 'components/candidate/GPTActionMenu/GPTActionMenu.component';
import Link from 'components/Link';
import Logo from 'components/Logo';
import { MatchesProgress } from 'components/candidate/matches';
import { AISearchSuggestionBanner } from 'components/AISearchSuggestionBanner/AISearchSuggestionBanner.component';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { TouchpointType } from 'lib/models/touchpoint';
import { useOpportunity } from 'lib/providers/OpportunityProvider';
import { useAuth } from 'lib/providers/AuthProvider';
import { PAGE_ROUTES } from 'lib/page-routes';

import styles from './Opportunity.module.scss';

const Opportunity: React.FC<{
  className?: string;
  floatingButtonsClassName?: string;
  prevButton?: ReactElement;
  nextButton?: ReactElement;
  handleNext?: () => void;
  handlePrev?: () => void;
}> = ({ className, floatingButtonsClassName, prevButton, nextButton, handleNext, handlePrev }): ReactElement => {
  const { isCandidate } = useAuth();
  const { opportunity, isOpportunityAppliedOrUnappliable, isOpportunityPublicPage, isMatchesPage, matchSlug, isSearchPage, handleShareURL } =
    useOpportunity();
  const { id, title, slug, description, touchpointable_type, apply_url } = opportunity;
  const isAnEvent = touchpointable_type === TouchpointType.Event;
  const [isExternalSignupBannerOpen, setExternalSignupBanner] = useState<boolean | null>(isOpportunityPublicPage && apply_url ? null : false);
  const externalSignupBannerRef = useRef<HTMLDivElement>(null);
  const showGPTActionMenu = !isAnEvent && !isOpportunityAppliedOrUnappliable;
  const [showAISearchSuggestionBanner, setShowAISearchSuggestionBanner] = useState(isCandidate && isOpportunityPublicPage);
  const showAISearchSuggestionBannerClosedRef = useRef(false);

  useEffect(() => {
    if (showAISearchSuggestionBannerClosedRef.current) return;
    if (isCandidate && isOpportunityPublicPage) setShowAISearchSuggestionBanner(true);
  }, [isCandidate, isOpportunityPublicPage]);

  const onCloseExternalSignupBanner = useCallback(() => {
    showAISearchSuggestionBannerClosedRef.current = true;
    setExternalSignupBanner(false);
  }, []);

  const application = useMemo(() => {
    return {
      id,
      title,
      touchpointable_type,
      slug,
    };
  }, [id, title, touchpointable_type, slug]);

  useVisibleOnIntersect({
    elRef: externalSignupBannerRef,
    threshold: 0,
    onIntersect: () => setExternalSignupBanner((flag) => (flag === null ? true : flag)),
    rootMargin: '0px',
  });

  const onCloseAISearchSuggestionBanner = useCallback(() => {
    setShowAISearchSuggestionBanner(false);
  }, []);

  return (
    <>
      {isCandidate ? (
        <header className={cx(styles.header, { ['border-less']: isMatchesPage })}>
          {isOpportunityPublicPage ? (
            <HamburgerNavMenu
              iconSize="large"
              className={styles.hamburger}
              triggerClassName={styles['hamburger-trigger']}
              menuListClassName={styles['hamburger-list']}
            />
          ) : (
            <Link href={PAGE_ROUTES.CANDIDATE_DASHBOARD} className={styles['home-link']}>
              <Logo name="huzzle-new" size="small" color="black" />
            </Link>
          )}
          <div className={styles['action-icons']}>
            <IconButton color="secondary" iconName="share" className={styles['icon-cta']} size="xmediumplus" onClick={handleShareURL} />
            {showGPTActionMenu && (
              <GPTActionMenu
                showIcon
                iconClassName={styles['gpt-action-menu-icon']}
                listClassName={styles['gpt-action-menu-list']}
                application={application}
              />
            )}
          </div>
        </header>
      ) : null}
      {isMatchesPage && <MatchesProgress className={styles['matches-progress']} currentMatchSlug={matchSlug} />}
      {isOpportunityPublicPage && showAISearchSuggestionBanner ? (
        <AISearchSuggestionBanner
          onClose={onCloseAISearchSuggestionBanner}
          className={styles['ai-search-suggestion-banner']}
          contentClassName={styles['ai-search-suggestion-banner-content']}
        />
      ) : (
        <OpportunityBanner className={styles['onboarding-banner']} />
      )}
      <section className={cx(styles.container, className, { [styles['search-page-opportunity']]: isSearchPage })}>
        <main className={styles.main}>
          <OpportunityHead className={styles['opportunity-head']} />
          {isOpportunityAppliedOrUnappliable && <SimilarOpportunities className={styles['similar-opportunities']} />}
          <div className={styles['about-opportunity']} ref={isOpportunityAppliedOrUnappliable ? externalSignupBannerRef : null}>
            {isAnEvent ? (
              <>
                <AboutEvent description={description} className={styles['about-event']} />
                <EventVenue className={styles['event-venue']} />
              </>
            ) : (
              <DescriptionSummaryTabs
                tabListClassName={styles['description-summary-tab-list']}
                tabPanelsClassName={styles['description-summary-tab-panels']}
                tabPanelSectionClassName={styles['description-summary-tab-panel-section']}
              />
            )}
          </div>
        </main>
        <article className={styles.sidebar}>
          <CreatedByInfo />
        </article>
        {!isOpportunityAppliedOrUnappliable && (
          <div className={styles['similar-opportunities-container']} ref={externalSignupBannerRef}>
            <SimilarOpportunities className={styles['similar-opportunities']} />
          </div>
        )}
      </section>
      <FloatingActionButtons
        className={cx(styles['floating-action-buttons'], floatingButtonsClassName, { [styles['search-page-floating-action-buttons']]: isSearchPage })}
        ctaClassName={cx({ [styles['floating-action-buttons-cta']]: isSearchPage })}
        prevButton={prevButton}
        nextButton={nextButton}
        handleNext={handleNext}
        handlePrev={handlePrev}
      />
      {!isCandidate && isExternalSignupBannerOpen && <ExternalSignupBanner onClose={onCloseExternalSignupBanner} />}
    </>
  );
};

export { Opportunity };
